import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, KeyRound, Mail } from "lucide-react";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [done, setDone] = useState(false);

  const requestLink = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submitNew = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== pw2) { toast.error("Passwords don't match"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2200);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "#253746" }} data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <img src={LOGO} alt="BoxHero" className="h-20 w-auto mx-auto mb-8" />
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {token ? (
            done ? (
              <div className="text-center" data-testid="reset-success">
                <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-slate-900">Password updated</h1>
                <p className="text-slate-500 mt-2">Taking you to sign in…</p>
              </div>
            ) : (
              <form onSubmit={submitNew} data-testid="reset-form">
                <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-4"><KeyRound className="w-6 h-6" /></div>
                <h1 className="text-2xl font-bold text-slate-900">Choose a new password</h1>
                <p className="text-slate-500 mt-1 mb-6 text-sm">Enter and confirm your new password below.</p>
                <label className="block text-sm font-medium text-slate-700 mb-1">New password</label>
                <input type="password" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="reset-pw-input"
                  placeholder="••••••••" className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
                <label className="block text-sm font-medium text-slate-700 mb-1">Confirm password</label>
                <input type="password" value={pw2} onChange={(e) => setPw2(e.target.value)} required data-testid="reset-pw2-input"
                  placeholder="••••••••" className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
                <button type="submit" disabled={loading} data-testid="reset-submit-btn"
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2">
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Update password
                </button>
              </form>
            )
          ) : sent ? (
            <div className="text-center" data-testid="forgot-sent">
              <Mail className="w-14 h-14 text-blue-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900">Check your email</h1>
              <p className="text-slate-500 mt-2">If an account exists for that address, we've sent a reset link. It expires in 2 hours.</p>
            </div>
          ) : (
            <form onSubmit={requestLink} data-testid="forgot-form">
              <div className="w-12 h-12 rounded-xl bg-blue-100 text-blue-600 flex items-center justify-center mb-4"><Mail className="w-6 h-6" /></div>
              <h1 className="text-2xl font-bold text-slate-900">Reset your password</h1>
              <p className="text-slate-500 mt-1 mb-6 text-sm">Enter your email and we'll send you a link to reset it.</p>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email-input"
                placeholder="you@brand.com" className="w-full px-4 py-3 border border-slate-300 rounded-lg mb-6 focus:outline-none focus:ring-2 focus:ring-blue-400/40" />
              <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold py-3 rounded-lg inline-flex items-center justify-center gap-2">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Send reset link
              </button>
            </form>
          )}
          <p className="text-center text-sm mt-6">
            <Link to="/login" className="text-blue-600 hover:underline font-medium" data-testid="back-to-login-link">Back to sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
